import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';

import BrandedPreviewShell from '../previews/BrandedPreviewShell';
import { triguardBrandProfile } from '../../branding/companyBrand';
import { createCXReferral } from '../../services/cxReferralService';

// The API-first MVP intentionally has no referral contact form or persisted customer model.
// These sentinel values satisfy the current stub API contract without collecting personal data.
const anonymousReferrerContact = 'anonymous-customer-session';
const unclaimedReferredContact = 'unclaimed-referral';

const ReferralRequestPreview: React.FC = () => {
  const brand = triguardBrandProfile;
  const [copied, setCopied] = useState(false);
  const [createdReferralUrl, setCreatedReferralUrl] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const getOrCreateReferralUrl = async () => {
    if (createdReferralUrl) return createdReferralUrl;
    setSaving(true);
    setError('');
    try {
      const referral = await createCXReferral({
        message: `A ${brand.companyName} customer shared their referral link.`,
        referredContact: unclaimedReferredContact,
        referredName: 'Friend',
        referrerContact: anonymousReferrerContact,
        referrerName: brand.customerName,
        tenantSlug: 'triguard',
      });
      if (referral.accessToken) localStorage.setItem('cxReferralAccessToken', referral.accessToken);
      const referralUrl = `${window.location.origin}/r/${referral.referralCode}`;
      setCreatedReferralUrl(referralUrl);
      return referralUrl;
    } catch {
      setError('We could not create your referral link. Please try again.');
      return '';
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const referralUrl = await getOrCreateReferralUrl();
    if (!referralUrl) return;
    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
    } catch {
      setCopied(false);
      setError('We could not copy the referral link. Please use the share option instead.');
    }
  };

  const handleShare = async (destination: 'facebook' | 'whatsapp' | 'share') => {
    const referralUrl = await getOrCreateReferralUrl();
    if (!referralUrl) return;
    if (destination === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (destination === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(`I thought you might like ${brand.companyName}. ${referralUrl}`)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (navigator.share) {
      await navigator.share({ title: `${brand.companyName} referral`, url: referralUrl });
      return;
    }
    await handleCopy();
  };

  return (
    <BrandedPreviewShell brand={brand} previewLabel="Referral request">
      <Card
        sx={{
          background: 'linear-gradient(155deg, var(--cx-brand-primary-dark) 0%, #103F46 100%)',
          borderRadius: 3,
          boxShadow: '0 18px 42px rgba(23, 26, 24, 0.18)',
          color: 'var(--color-base-white)',
          p: { xs: 2.5, sm: 3 },
        }}
      >
        <Stack spacing={2.25} sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              alignItems: 'center',
              alignSelf: 'center',
              bgcolor: 'rgba(255,255,255,0.12)',
              borderRadius: 2,
              color: 'var(--color-base-white)',
              display: 'flex',
              height: 52,
              justifyContent: 'center',
              width: 52,
            }}
          >
            <CardGiftcardRoundedIcon />
          </Box>

          <Stack spacing={0.75}>
            <Typography variant="h4" sx={{ color: 'inherit', fontSize: { xs: 30, sm: 34 }, fontWeight: 900 }}>
              Refer a friend!
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 }}>
              Happy with TriGuard? Share your link with someone who could benefit from our service.
              They save $50, and you earn $50 after their first paid visit.
            </Typography>
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} sx={{ justifyContent: 'center', py: 1 }}>
            {[
              { label: copied ? 'Copied' : 'Copy link', icon: ContentCopyRoundedIcon, action: handleCopy },
              { label: 'Share on Facebook', icon: FacebookRoundedIcon, action: () => handleShare('facebook') },
              { label: 'Share on WhatsApp', icon: WhatsAppIcon, action: () => handleShare('whatsapp') },
              { label: 'Share referral', icon: ShareRoundedIcon, action: () => handleShare('share') },
            ].map(({ action, icon: Icon, label }) => (
              <IconButton
                aria-label={label}
                disabled={saving}
                key={label}
                onClick={() => void action().catch(() => undefined)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.55)',
                  color: 'var(--color-base-white)',
                  height: 52,
                  width: 52,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.26)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.72)' },
                }}
              >
                <Icon />
              </IconButton>
            ))}
          </Stack>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.16)' }} />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
            <Button
              component={RouterLink}
              startIcon={<DashboardRoundedIcon />}
              sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 800 }}
              to="/preview/referral-dashboard"
              variant="text"
            >
              View my rewards
            </Button>
          </Stack>
        </Stack>
      </Card>
    </BrandedPreviewShell>
  );
};

export default ReferralRequestPreview;
